import { generate } from '@ant-design/colors'

import { readableFill } from './contrast'
import type { ThemeMode } from './palette'
import { brandSeed } from './seed'

const primaryShades = (primary: string, mode: ThemeMode) => {
  if (mode === 'dark') {
    const palette = generate(primary, { theme: 'dark' })

    return { base: palette[5], hover: palette[6], press: palette[4] }
  }

  const palette = generate(primary)

  return { base: palette[5], hover: palette[4], press: palette[6] }
}

/**
 * Maps `colorPrimary` to the `--brand-teal*` CSS-var family consumed by the
 * non-AntD surfaces (kanban, calendar, audit log, command palette, banners)
 * and the `!important` repins in `antd-guardrails.css`.
 *
 * Only the SOLID accents come from `generate()` — its indices are exactly the
 * ones AntD's `genColorMapToken` assigns (verified against
 * `antd/es/theme/themes/default/colors.js`), so these match AntD's own tokens:
 *   colorPrimary       = generate(primary)[5]   → --brand-teal       (base)
 *   colorPrimaryHover  = generate(primary)[4]   → --brand-teal-hover
 *   colorPrimaryActive = generate(primary)[6]   → --brand-teal-press
 *
 * The light TINTS are intentionally NOT taken from the palette: for a dark
 * seed like #0F766E, `generate()[0]` is a muddy grey-green (#a8b5b2), not a
 * clean tint — which is why those shades used to be hand-pinned. Instead the
 * tints (`--brand-teal-tint`/`-tint-2`/`-soft`/`-mid`) are
 * `color-mix`ed from `--brand-teal` into white/transparent in tokens.css, so
 * they stay clean for ANY seed (teal → mint, red → pink) and need no JS.
 */
export const brandCssVars = (
  primary: string = brandSeed.colorPrimary,
  mode: ThemeMode = 'light',
  fillSeed: string = primary
) => {
  const accent = primaryShades(primary, mode)
  const fill = readableFill(primaryShades(fillSeed, mode))

  return {
    '--brand-teal': accent.base,
    '--brand-teal-hover': accent.hover,
    '--brand-teal-press': accent.press,
    '--brand-fill': fill.base,
    '--brand-fill-hover': fill.hover,
    '--brand-fill-press': fill.press,
    '--brand-on-fill': fill.onFill
  } as const
}

/**
 * Writes the brand CSS vars onto `:root` (`document.documentElement`) so the
 * CSS-var layer tracks the live `colorPrimary` of the active mode (called from
 * `ThemeProviderWrapper`; this app has no org-settings cascade, unlike the ebs
 * shell). No-op outside the browser (SSR / tests).
 *
 * Pass the mode's primary; defaults to the seed when omitted.
 */
export const applyBrandCssVars = (
  primary?: string,
  mode?: ThemeMode,
  fillSeed?: string
) => {
  if (typeof document === 'undefined') return

  const root = document.documentElement

  Object.entries(brandCssVars(primary, mode, fillSeed)).forEach(
    ([name, value]) => root.style.setProperty(name, value)
  )
}
